import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled
} from '@mui/material';
import { dateFormatter, getReccurssiveObjectKeys } from '../../utils';
import { evaluate, parse, MathNode } from 'mathjs';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.9),
  whiteSpace: 'nowrap',
  fontSize: '15px',
  fontWeight: 500,
  '&.MuiTableCell-head': {
    position: 'sticky',
    top: 0,
    backgroundColor: 'lightblue',
    zIndex: 2
  }
}));

const RESERVED = new Set([
  'sqrt', 'log', 'ln', 'abs', 'sin', 'cos', 'tan',
  'pi', 'e', 'min', 'max', 'round', 'floor', 'ceil', '^', 'exp'
]);

export const ResultEntryTable = ({
  data,
  columns,
  onRowClick,
  containerSX,
  sx,
  size,
  showDeleteAction,
  showInputField,
  handleInputChange,
  inputValues,
  currentAction,
}) => {
  const [variables, setVariables] = useState({});
  const [overrides, setOverrides] = useState({});
  const [lastSentValues, setLastSentValues] = useState({});
  // Extract all variable names from formulas and initialize them in state if not already present
  useEffect(() => {
    const varsNeeded = new Set();

    data?.forEach(item => {
      if (item.res_formula) {
        const matches = item.res_formula.match(/[a-zA-Z_]\w*/g);
        matches?.forEach(varName => {
          if (!RESERVED.has(varName)) {
            varsNeeded.add(varName);
          }
        });
      } else if (item.var) {
        varsNeeded.add(item.var);
      }
    });

    setVariables(prev => {
      const newVars = { ...prev };
      varsNeeded.forEach(v => {
        if (!(v in newVars)) {
          newVars[v] = 0;
        }
      });
      return newVars;
    });
  }, [data]);

  const handleChange = (variable, value) => {
    const parsed = parseFloat(value);
    setVariables(prev => ({
      ...prev,
      [variable]: isNaN(parsed) ? 0 : parsed
    }));
  };

  const calculatedValues = useMemo(() => {
    const results = { ...variables };
    console.log(results);
    for (const field of data) {
      if (field.res_formula && field.var) {
        try {
          // Collect variable names used in the formula
          const matches = field.res_formula.match(/[a-zA-Z_]\w*/g) || [];
          const requiredVars = matches.filter(v => !RESERVED.has(v));

          // Check if any required variable is missing or undefined
          const missing = requiredVars.some(v => results[v] === undefined || results[v] === '');

          if (missing) {
            results[field.var] = '';
          } else {
            const result = evaluate(field.res_formula, results);
            results[field.var] =
              typeof result === 'number' && isFinite(result)
                ? Math.round(result * 100) / 100
                : 'Awaiting input';
          }
        } catch (error) {
          console.error(`Error evaluating formula "${field.res_formula}":`, error);
          results[field.var] = 'Awaiting input';
        }
      }
    }

    return results;
  }, [variables, data]);
// Automatically call handleInputChange when calculated values change
useEffect(() => {
  if (!data || !handleInputChange) return;

  const updates = {};
  let hasUpdates = false;

  data.forEach(row => {
    if (row.res_formula && row.var) {
      const calculatedValue = calculatedValues?.[row.var];
      const shouldUpdate = (
        calculatedValue !== undefined &&
        typeof calculatedValue === 'number' &&
        calculatedValue !== lastSentValues[row.var]
      );

      if (shouldUpdate) {
        updates[row.var] = calculatedValue;
        handleInputChange(row._id, 'result', calculatedValue);
        hasUpdates = true;
      }
    }
  });

  if (hasUpdates) {
    setLastSentValues(prev => ({...prev, ...updates}));
  }
}, [calculatedValues]);



  return (
    <TableContainer sx={{ maxHeight: '250px', ...containerSX }}>
      <MuiTable sx={sx} size={size || 'medium'}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'lightblue' }}>
            <StyledTableCell key="sr.no.-cell" sx={{ fontWeight: 'bold' }}>
              Sr. No.
            </StyledTableCell>
            {columns.map((column, index) => (
              <StyledTableCell sx={{ fontWeight: 'bold' }} key={`header-cell-${index}`}>
                {column.header}
              </StyledTableCell>
            ))}
            {showDeleteAction && (
              <StyledTableCell key="action-cell" sx={{ fontWeight: 'bold' }}>
                Action
              </StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((row, rowIndex) => (
            <TableRow
              {...(onRowClick && {
                onClick: () => onRowClick(row)
              })}
              sx={{
                backgroundColor: rowIndex % 2 ? 'grey.200' : 'none',
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: 'grey.300'
                },
                transition: 'all 0.1s ease-in'
              }}
              key={row._id || rowIndex}
            >
              <StyledTableCell key={`${row._id}-cell0`}>{rowIndex + 1}</StyledTableCell>
              {columns.map(({ header, accessor, sx }, colIndex) => (
                <StyledTableCell sx={sx} key={`${row._id}-cell${colIndex + 1}`}>
                  {header === 'Result' && showInputField ? (
                    row.res_formula === null ? (
                      <input
                        type="number"
                        value={variables[row.var] || ''}
                        // onChange={(e) => handleChange(row.var, e.target.value)}
                        onChange={(e) => {
                          handleChange(row.var, e.target.value);
                          handleInputChange(row._id, 'result', e.target.value);
                        }}
                        disabled={!['edit', 'new'].includes(currentAction)}
                        style={{
                          width: '60px',
                          padding: '4px 8px',
                          boxSizing: 'border-box',
                          fontSize: '14px',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}
                      />
                    ) : (
                      <input
                        type="number"
                        value={calculatedValues?.[row?.var] ?? row?.result}
                        onChange={(e) => {
                          // This will still work if you need manual input
                          handleChange(row.var, e.target.value);
                          handleInputChange(row._id, 'result', e.target.value);
                         
                        }}
                        disabled={!['edit', 'new'].includes(currentAction)}
                        style={{
                          width: '60px',
                          padding: '4px 8px',
                          boxSizing: 'border-box',
                          fontSize: '14px',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}
                      />
                      // String(calculatedValues?.[row?.var] ?? row?.result)
                    )
                  ) : typeof accessor === 'function' ? (
                    accessor(row) || '-'
                  ) : (
                    <>
                      {header === 'Created At' || header === 'Date'
                        ? dateFormatter(getReccurssiveObjectKeys(row, accessor)) || '-'
                        : getReccurssiveObjectKeys(row, accessor) || '-'}
                    </>
                  )}
                </StyledTableCell>
              ))}
              {showDeleteAction && (
                <StyledTableCell key={`${row._id}-delete`}>
                  <Button color="error" size="small">
                    Delete
                  </Button>
                </StyledTableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
